import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ArrowLeft, 
  ArrowUpRight, 
  ArrowDownRight, 
  MapPin, 
  FolderOpen, 
  Hash,
  Ruler,
  Package,
  FileText,
  Printer,
  Wrench,
  Ban,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  available: { label: 'Available', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', icon: null },
  checked_out: { label: 'Checked Out', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', icon: null },
  under_maintenance: { label: 'Under Maintenance', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20', icon: Wrench },
  unavailable: { label: 'Unavailable', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20', icon: Ban },
};

const ItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const { settings } = useSettings();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItem = useCallback(async () => {
    try {
      // Use public endpoint if not authenticated
      const endpoint = isAuthenticated 
        ? `${API_URL}/api/items/${id}` 
        : `${API_URL}/api/items/${id}/public`;
      
      const config = isAuthenticated ? { headers: getAuthHeaders() } : {};
      const response = await axios.get(endpoint, config);
      setItem(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Item not found');
      toast.error('Failed to load item details');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to check out items');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/items/${id}/checkout`, {}, { headers: getAuthHeaders() });
      toast.success('Item checked out successfully');
      fetchItem();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check out');
    }
  };

  const handleCheckin = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to check in items');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/items/${id}/checkin`, {}, { headers: getAuthHeaders() });
      toast.success('Item checked in successfully');
      fetchItem();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check in');
    }
  };

  const handlePrintQR = () => {
    const printWindow = window.open('', '_blank');
    const qrUrl = `${window.location.origin}/item/${item.id}`;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${item.name}</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
            .qr-container { display: inline-block; padding: 20px; border: 2px solid #000; margin: 10px; }
            .item-name { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
            .item-number { font-size: 14px; color: #666; margin-bottom: 15px; }
            .qr-code { margin: 15px 0; }
            .scan-text { font-size: 12px; color: #888; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="item-name">${item.name}</div>
            <div class="item-number">${item.item_number}</div>
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}" alt="QR Code" />
            </div>
            <div class="scan-text">Scan to view item details</div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const canCheckout = item && !item.checked_out && item.status !== 'under_maintenance' && item.status !== 'unavailable';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <div className="text-center space-y-4">
          <Package className="w-16 h-16 text-muted-foreground mx-auto" />
          <h1 className="text-2xl font-bold">Item Not Found</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.available;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-background" data-testid="item-detail-page">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="w-8 h-8 rounded object-contain" />
            ) : null}
            <span className="font-semibold">{settings?.school_name || 'SchoolAV'}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Item Header */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* QR Code */}
          <div className="flex-shrink-0">
            <Card className="border-border/50 p-4">
              <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG 
                  value={`${window.location.origin}/item/${item.id}`}
                  size={150}
                  level="H"
                />
              </div>
              <Button onClick={handlePrintQR} variant="outline" size="sm" className="w-full mt-3" data-testid="print-qr-detail-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print QR
              </Button>
            </Card>
          </div>

          {/* Item Info */}
          <div className="flex-1 space-y-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{item.name}</h1>
              <code className="text-lg text-muted-foreground font-mono">{item.item_number}</code>
            </div>

            <Badge variant="secondary" className={`${statusConfig.color} text-sm`}>
              {StatusIcon && <StatusIcon className="w-3 h-3 mr-1" />}
              {statusConfig.label}
            </Badge>

            {item.checked_out && item.checked_out_by_name && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                Checked out by {item.checked_out_by_name}
                {item.checked_out_at && ` on ${new Date(item.checked_out_at).toLocaleDateString()}`}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              {item.checked_out ? (
                <Button onClick={handleCheckin} className="flex-1" data-testid="checkin-detail-btn">
                  <ArrowDownRight className="w-4 h-4 mr-2" />
                  Check In
                </Button>
              ) : canCheckout ? (
                <Button onClick={handleCheckout} className="flex-1" data-testid="checkout-detail-btn">
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Check Out
                </Button>
              ) : (
                <Button disabled className="flex-1">
                  {item.status === 'under_maintenance' ? 'Under Maintenance' : 'Unavailable'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{item.category_name || 'Uncategorized'}</p>
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Storage Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{item.storage_location_name || 'Unknown'}</p>
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Hash className="w-4 h-4" />
                Quantity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{item.quantity}</p>
            </CardContent>
          </Card>

          {item.length && (
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Ruler className="w-4 h-4" />
                  Length
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold">{item.length}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Notes */}
        {item.notes && (
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground whitespace-pre-wrap">{item.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Login prompt for non-authenticated users */}
        {!isAuthenticated && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="pt-6">
              <div className="text-center space-y-3">
                <p className="text-muted-foreground">Login to check out or check in this item</p>
                <Button onClick={() => navigate('/login')} data-testid="login-prompt-btn">
                  Login to Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ItemDetail;
